import { contactoStatusApi } from "./api";

export interface ProductCategory {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  color?: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateProductCategoryData {
  slug: string;
  name: string;
  description?: string;
  color?: string;
  is_default?: boolean;
  is_active?: boolean | number;
}

export interface UpdateProductCategoryData {
  slug: string;
  name?: string;
  description?: string;
  color?: string;
  is_default?: boolean;
  is_active?: boolean | number;
}

export interface ProductCategoriesApiResponse {
  statusCode: number;
  body: {
    success: boolean;
    data: ProductCategory[];
    message?: string;
  };
}

export interface ProductCategoryApiResponse {
  statusCode: number;
  body: {
    success: boolean;
    data: ProductCategory;
    message?: string;
  };
}

export interface GetProductCategoriesParams {
  slug: string;
  is_active?: boolean | number;
  is_default?: boolean;
}

class ProductCategoryService {
  async getProductCategories(
    params: GetProductCategoriesParams,
  ): Promise<ProductCategory[]> {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("slug", params.slug);

      if (params.is_active !== undefined && params.is_active !== null) {
        const isActiveValue =
          params.is_active === true
            ? 1
            : params.is_active === false
              ? 0
              : params.is_active;
        queryParams.append("is_active", isActiveValue.toString());
      }

      if (params.is_default !== undefined) {
        queryParams.append("is_default", params.is_default.toString());
      }

      const url = `/product-categories?${queryParams.toString()}`;
      const response = await contactoStatusApi.get(url);
      const apiResponse = response.data;

      if (Array.isArray(apiResponse)) {
        return apiResponse;
      } else if (apiResponse.success && Array.isArray(apiResponse.data)) {
        return apiResponse.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async getProductCategoryById(
    id: string,
    slug: string,
  ): Promise<ProductCategory> {
    try {
      const url = `/product-categories/${id}?slug=${slug}`;
      const response = await contactoStatusApi.get(url);
      const apiResponse = response.data;

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async createProductCategory(
    categoryData: CreateProductCategoryData,
  ): Promise<ProductCategory> {
    try {
      const dataToSend = {
        ...categoryData,
        is_active:
          categoryData.is_active === true
            ? 1
            : categoryData.is_active === false
              ? 0
              : categoryData.is_active || 1,
        is_default: categoryData.is_default ?? false,
      };

      const response = await contactoStatusApi.post(
        "/product-categories",
        dataToSend,
      );
      const apiResponse = response.data;

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async updateProductCategory(
    id: string,
    categoryData: UpdateProductCategoryData,
  ): Promise<ProductCategory> {
    try {
      const dataToSend: any = { ...categoryData };
      if (categoryData.is_active !== undefined) {
        dataToSend.is_active =
          categoryData.is_active === true
            ? 1
            : categoryData.is_active === false
              ? 0
              : categoryData.is_active;
      }

      const response = await contactoStatusApi.put(
        `/product-categories/${id}`,
        dataToSend,
      );
      const apiResponse = response.data;

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteProductCategory(id: string, slug: string): Promise<boolean> {
    try {
      const response = await contactoStatusApi.delete(
        `/product-categories/${id}?slug=${slug}`,
      );
      const apiResponse = response.data;

      if (apiResponse.success) {
        return true;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return true;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  getActiveCategories(categories: ProductCategory[]): ProductCategory[] {
    return categories.filter((category) => category.is_active);
  }

  getDefaultCategory(
    categories: ProductCategory[],
  ): ProductCategory | undefined {
    return categories.find((category) => category.is_default);
  }

  getCategoryById(
    categories: ProductCategory[],
    id: string,
  ): ProductCategory | undefined {
    return categories.find((category) => category.id === id);
  }

  searchCategories(
    categories: ProductCategory[],
    searchTerm: string,
  ): ProductCategory[] {
    const term = searchTerm.toLowerCase();
    return categories.filter(
      (category) =>
        category.name.toLowerCase().includes(term) ||
        category.description?.toLowerCase().includes(term),
    );
  }

  mapToLocalFormat(category: ProductCategory): any {
    return {
      id: category.id,
      name: category.name,
      description: category.description || "",
      color: category.color || "#6b7280",
      createdAt: category.created_at,
      updatedAt: category.updated_at,
    };
  }

  mapFromLocalFormat(
    localCategory: any,
    slug: string,
  ): CreateProductCategoryData {
    return {
      slug: slug,
      name: localCategory.name,
      description: localCategory.description,
      color: localCategory.color,
      is_active: 1,
    };
  }
}

export const productCategoryService = new ProductCategoryService();
